import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import axios from 'axios';
import foodTrackingService from '../../services/foodTrackingService';

const API_URL = import.meta.env.VITE_API_URL || 'https://fiifit.ro';

interface FoodIngredient {
  name: string;
  estimatedGrams: number;
  nutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
}

interface FoodVisionScannerProps {
  onScanSuccess: (food: any) => void;
  onClose: () => void;
  mealType?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  date?: string;
  onDirectAdd?: () => void;
}

export default function FoodVisionScanner({ 
  onScanSuccess, 
  onClose, 
  mealType, 
  date, 
  onDirectAdd 
}: FoodVisionScannerProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analyzedFood, setAnalyzedFood] = useState<any>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [editingGrams, setEditingGrams] = useState(false);
  const [ingredientGrams, setIngredientGrams] = useState<Record<number, number>>({});

  // Editing ingredient names
  const [editingNameIdx, setEditingNameIdx] = useState<number | null>(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [loadingNameIdx, setLoadingNameIdx] = useState<number | null>(null);

  // Editing nutrition values per ingredient (per 100g)
  const [editingNutritionIdx, setEditingNutritionIdx] = useState<number | null>(null);
  const [editNutritionValues, setEditNutritionValues] = useState<{
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  }>({ calories: 0, protein: 0, carbs: 0, fat: 0 });

  // Editing total calories directly (for simple foods without ingredients)
  const [editingTotalCalories, setEditingTotalCalories] = useState(false);
  const [editTotalValues, setEditTotalValues] = useState<{
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  }>({ calories: 0, protein: 0, carbs: 0, fat: 0 });

  const dataURItoBlob = (dataURI: string) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const optimizeImage = async (dataUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        const maxSize = 1024;
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else {
            width = (width / height) * maxSize;
            height = maxSize;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Cannot get canvas context')); return; }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });
  };

  const analyzeImage = async (imageData: string) => {
    setLoading(true);
    setError(null);
    try {
      const blob = dataURItoBlob(imageData);
      const formData = new FormData();
      formData.append('image', blob, 'food.jpg');
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/api/food-tracking/analyze-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      console.log('[FOOD VISION] Analysis result:', response.data);
      const food = response.data;
      // Inițializează gramele din estimările AI
      if (food.ingredients && food.ingredients.length > 0) {
        const initialGrams: Record<number, number> = {};
        food.ingredients.forEach((ing: FoodIngredient, idx: number) => {
          initialGrams[idx] = ing.estimatedGrams;
        });
        setIngredientGrams(initialGrams);
      }
      if (mealType && date && onDirectAdd) {
        setAnalyzedFood(food);
        setShowConfirm(true);
      } else {
        onScanSuccess(food);
      }
    } catch (err: any) {
      console.error('[FOOD VISION] Error:', err);
      setError(err.response?.data?.error || err.message || 'Eroare la analiza imaginii');
    } finally {
      setLoading(false);
    }
  };

  // Calculează totalul nutrițional bazat pe gramele curente
  const calcTotals = () => {
    if (!analyzedFood) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    
    const ingredients: FoodIngredient[] = analyzedFood.ingredients;
    if (!ingredients || ingredients.length === 0) {
      // Fallback: folosește servingSize și nutrition per 100g
      const grams = analyzedFood.servingSize || 100;
      const n = analyzedFood.nutrition || {};
      return {
        calories: Math.round((n.calories || 0) * grams / 100),
        protein: Math.round((n.protein || 0) * grams / 100 * 10) / 10,
        carbs: Math.round((n.carbs || 0) * grams / 100 * 10) / 10,
        fat: Math.round((n.fat || 0) * grams / 100 * 10) / 10,
      };
    }

    let totalCal = 0, totalProt = 0, totalCarbs = 0, totalFat = 0;
    ingredients.forEach((ing, idx) => {
      const grams = ingredientGrams[idx] ?? ing.estimatedGrams;
      totalCal += (ing.nutrition.calories / 100) * grams;
      totalProt += (ing.nutrition.protein / 100) * grams;
      totalCarbs += (ing.nutrition.carbs / 100) * grams;
      totalFat += (ing.nutrition.fat / 100) * grams;
    });
    return {
      calories: Math.round(totalCal),
      protein: Math.round(totalProt * 10) / 10,
      carbs: Math.round(totalCarbs * 10) / 10,
      fat: Math.round(totalFat * 10) / 10,
    };
  };

  const adjustGrams = (idx: number, delta: number) => {
    setIngredientGrams(prev => {
      const current = prev[idx] ?? (analyzedFood?.ingredients?.[idx]?.estimatedGrams || 100);
      const newVal = Math.max(10, current + delta);
      return { ...prev, [idx]: newVal };
    });
  };

  // ============================================================
  // EDIT INGREDIENT NAME - fetch new nutrition from AI
  // ============================================================
  const handleStartEditName = (idx: number) => {
    const ing = analyzedFood?.ingredients?.[idx];
    if (!ing) return;
    setEditNameValue(ing.name);
    setEditingNameIdx(idx);
  };

  const handleCancelEditName = () => {
    setEditingNameIdx(null);
    setEditNameValue('');
  };

  const handleConfirmEditName = async (idx: number) => {
    if (!analyzedFood || !editNameValue.trim()) return;
    const oldName = analyzedFood.ingredients[idx].name;
    const newName = editNameValue.trim();
    
    if (newName === oldName) {
      handleCancelEditName();
      return;
    }

    setLoadingNameIdx(idx);
    try {
      // Call AI to get nutrition for the new food name
      const aiResult = await foodTrackingService.getNutritionFromAI(newName);
      
      // Extract nutrition per 100g from AI result
      let newNutrition = { calories: 0, protein: 0, carbs: 0, fat: 0 };
      
      if (aiResult.ingredients && aiResult.ingredients.length > 0) {
        // Use first ingredient's nutrition (per 100g)
        const firstIng = aiResult.ingredients[0];
        newNutrition = {
          calories: parseFloat(String(firstIng.calories ?? 0)) || 0,
          protein: parseFloat(String(firstIng.protein ?? 0)) || 0,
          carbs: parseFloat(String(firstIng.carbs ?? 0)) || 0,
          fat: parseFloat(String(firstIng.fats ?? firstIng.fat ?? 0)) || 0,
        };
      }

      // Update the ingredient in analyzedFood
      const updatedIngredients = [...analyzedFood.ingredients];
      updatedIngredients[idx] = {
        ...updatedIngredients[idx],
        name: newName,
        nutrition: newNutrition,
      };

      setAnalyzedFood({
        ...analyzedFood,
        ingredients: updatedIngredients,
      });

      handleCancelEditName();
    } catch (err: any) {
      console.error('[FOOD VISION] Error fetching nutrition for new name:', err);
      // If AI fails, just update the name and keep old nutrition
      const updatedIngredients = [...analyzedFood.ingredients];
      updatedIngredients[idx] = {
        ...updatedIngredients[idx],
        name: newName,
      };
      setAnalyzedFood({
        ...analyzedFood,
        ingredients: updatedIngredients,
      });
      handleCancelEditName();
    } finally {
      setLoadingNameIdx(null);
    }
  };

  // ============================================================
  // EDIT NUTRITION VALUES PER INGREDIENT (per 100g)
  // ============================================================
  const handleStartEditNutrition = (idx: number) => {
    const ing = analyzedFood?.ingredients?.[idx];
    if (!ing) return;
    setEditNutritionValues({
      calories: ing.nutrition.calories,
      protein: ing.nutrition.protein,
      carbs: ing.nutrition.carbs,
      fat: ing.nutrition.fat,
    });
    setEditingNutritionIdx(idx);
  };

  const handleCancelEditNutrition = () => {
    setEditingNutritionIdx(null);
  };

  const handleConfirmEditNutrition = (idx: number) => {
    if (!analyzedFood) return;
    const updatedIngredients = [...analyzedFood.ingredients];
    updatedIngredients[idx] = {
      ...updatedIngredients[idx],
      nutrition: { ...editNutritionValues },
    };
    setAnalyzedFood({
      ...analyzedFood,
      ingredients: updatedIngredients,
    });
    setEditingNutritionIdx(null);
  };

  // ============================================================
  // EDIT TOTAL NUTRITION (for simple foods without ingredients)
  // ============================================================
  const handleStartEditTotalNutrition = () => {
    const totals = calcTotals();
    setEditTotalValues({
      calories: totals.calories,
      protein: totals.protein,
      carbs: totals.carbs,
      fat: totals.fat,
    });
    setEditingTotalCalories(true);
  };

  const handleConfirmEditTotalNutrition = () => {
    if (!analyzedFood) return;
    const hasIngredients = analyzedFood.ingredients && analyzedFood.ingredients.length > 0;
    
    if (!hasIngredients) {
      // Update the main nutrition directly
      const grams = analyzedFood.servingSize || 100;
      const factor = grams > 0 ? 100 / grams : 1;
      setAnalyzedFood({
        ...analyzedFood,
        nutrition: {
          ...analyzedFood.nutrition,
          calories: Math.round(editTotalValues.calories * factor),
          protein: Math.round(editTotalValues.protein * factor * 10) / 10,
          carbs: Math.round(editTotalValues.carbs * factor * 10) / 10,
          fat: Math.round(editTotalValues.fat * factor * 10) / 10,
        },
      });
    }
    setEditingTotalCalories(false);
  };

  const handleDirectAdd = async () => {
    if (!analyzedFood || !mealType || !date) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const totals = calcTotals();
      const hasIngredients = analyzedFood.ingredients && analyzedFood.ingredients.length > 0;
      const ingredientTotal = hasIngredients
        ? analyzedFood.ingredients.reduce((sum: number, ing: FoodIngredient, idx: number) => {
            return sum + (ingredientGrams[idx] ?? ing.estimatedGrams);
          }, 0)
        : 0;
      const totalGrams = hasIngredients
        ? (ingredientTotal > 0 ? ingredientTotal : analyzedFood.servingSize || 100)
        : (analyzedFood.servingSize || 100);

      // Build a proper ISO datetime with timezone offset so the server stores the correct UTC time
      const now = new Date();
      const [year, month, day] = date.split('-').map(Number);
      const localDate = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());
      const consumedAt = localDate.toISOString();

      await axios.post(
        `${API_URL}/api/food-tracking/entries`,
        {
          foodName: analyzedFood.name,
          brand: analyzedFood.brand,
          servingSize: totalGrams,
          servingUnit: 'g',
          quantity: totalGrams,
          nutrition: {
            calories: totals.calories,
            protein: totals.protein,
            carbs: totals.carbs,
            fat: totals.fat,
            fiber: analyzedFood.nutrition?.fiber,
            sugar: analyzedFood.nutrition?.sugar,
            sodium: analyzedFood.nutrition?.sodium,
          },
          mealType,
          date: consumedAt,
          imageUrl: analyzedFood.imageUrl,
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      console.log('[FOOD VISION] Product added directly');
      if (onDirectAdd) onDirectAdd();
      onClose();
    } catch (err: any) {
      console.error('[FOOD VISION] Error adding entry:', err);
      setError(err.response?.data?.error || 'Eroare la adăugarea produsului');
    } finally {
      setLoading(false);
    }
  };

  const handleEditBeforeAdd = () => {
    if (!analyzedFood) return;
    const totals = calcTotals();
    const hasIngredients = analyzedFood.ingredients && analyzedFood.ingredients.length > 0;
    const ingredientTotal = hasIngredients
      ? analyzedFood.ingredients.reduce((sum: number, ing: FoodIngredient, idx: number) => {
          return sum + (ingredientGrams[idx] ?? ing.estimatedGrams);
        }, 0)
      : 0;
    const totalGrams = hasIngredients
      ? (ingredientTotal > 0 ? ingredientTotal : analyzedFood.servingSize || 100)
      : (analyzedFood.servingSize || 100);

    // Normalize nutrition to per-100g so FoodLogger multiplier works correctly
    const factor = totalGrams > 0 ? 100 / totalGrams : 1;
    const normalizedFood = {
      ...analyzedFood,
      servingSize: totalGrams,
      servingUnit: 'g',
      nutrition: {
        calories: Math.round(totals.calories * factor),
        protein: Math.round(totals.protein * factor * 10) / 10,
        carbs: Math.round(totals.carbs * factor * 10) / 10,
        fat: Math.round(totals.fat * factor * 10) / 10,
        fiber: analyzedFood.nutrition?.fiber,
        sugar: analyzedFood.nutrition?.sugar,
        sodium: analyzedFood.nutrition?.sodium,
      },
    };
    onScanSuccess(normalizedFood);
  };

  const handleTakePhoto = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90, allowEditing: false,
        resultType: CameraResultType.DataUrl, source: CameraSource.Camera,
      });
      if (image.dataUrl) {
        setPreview(image.dataUrl);
        const optimized = await optimizeImage(image.dataUrl);
        await analyzeImage(optimized);
      }
    } catch (err: any) {
      if (err.message !== 'User cancelled photos app') setError('Eroare la accesarea camerei');
    }
  };

  const handleUploadPhoto = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90, allowEditing: false,
        resultType: CameraResultType.DataUrl, source: CameraSource.Photos,
      });
      if (image.dataUrl) {
        setPreview(image.dataUrl);
        const optimized = await optimizeImage(image.dataUrl);
        await analyzeImage(optimized);
      }
    } catch (err: any) {
      if (err.message !== 'User cancelled photos app') setError('Eroare la selectarea imaginii');
    }
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Te rog selectează o imagine'); return; }
    if (file.size > 20 * 1024 * 1024) { setError('Imaginea este prea mare (max 20MB)'); return; }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result as string;
      setPreview(dataUrl);
      const optimized = await optimizeImage(dataUrl);
      await analyzeImage(optimized);
    };
    reader.readAsDataURL(file);
  };

  // ============================================================
  // CONFIRM MODAL - cu ingrediente editabile (nume, grame, nutriție)
  // ============================================================
  if (showConfirm && analyzedFood) {
    const totals = calcTotals();
    const hasIngredients = analyzedFood.ingredients && analyzedFood.ingredients.length > 0;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center sm:p-4 overflow-hidden">
        <div className="bg-white rounded-t-2xl sm:rounded-xl max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <span className="text-2xl">✅</span>
              Produs Identificat
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* Preview imagine */}
            {preview && (
              <img src={preview} alt="Scanned food" className="w-full max-h-40 object-cover rounded-lg" />
            )}

            {/* Nume produs */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
              <h3 className="text-lg font-bold text-gray-900">{analyzedFood.name}</h3>
              {analyzedFood.brand && <p className="text-sm text-gray-500">{analyzedFood.brand}</p>}
              {analyzedFood.aiDescription && (
                <p className="text-xs text-gray-500 mt-1 italic">{analyzedFood.aiDescription}</p>
              )}
            </div>

            {/* Info banner about editing */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <span className="text-amber-500 text-lg">💡</span>
                <p className="text-xs text-amber-800">
                  <strong>Poți corecta rezultatele AI:</strong> Apasă pe numele unui ingredient pentru a-l schimba, 
                  sau pe valorile nutriționale pentru a le edita manual. AI-ul va recalcula automat nutriția când schimbi un ingredient.
                </p>
              </div>
            </div>

            {/* INGREDIENTE CU GRAMAJE - dacă există */}
            {hasIngredients && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    🥗 Ingrediente estimate
                  </h4>
                  <button
                    onClick={() => setEditingGrams(!editingGrams)}
                    className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                      editingGrams
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    {editingGrams ? '✓ Gata' : '⚖️ Modifică cantități'}
                  </button>
                </div>

                <div className="space-y-2">
                  {analyzedFood.ingredients.map((ing: FoodIngredient, idx: number) => {
                    const grams = ingredientGrams[idx] ?? ing.estimatedGrams;
                    const ingCal = Math.round((ing.nutrition.calories / 100) * grams);
                    const ingProt = Math.round((ing.nutrition.protein / 100) * grams * 10) / 10;
                    const ingCarbs = Math.round((ing.nutrition.carbs / 100) * grams * 10) / 10;
                    const ingFat = Math.round((ing.nutrition.fat / 100) * grams * 10) / 10;
                    const isEditingName = editingNameIdx === idx;
                    const isLoadingName = loadingNameIdx === idx;
                    const isEditingNutrition = editingNutritionIdx === idx;

                    return (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        {/* Ingredient Name - Editable */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            {isEditingName ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={editNameValue}
                                  onChange={(e) => setEditNameValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleConfirmEditName(idx);
                                    if (e.key === 'Escape') handleCancelEditName();
                                  }}
                                  className="flex-1 px-2 py-1 border border-blue-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                  autoFocus
                                  disabled={isLoadingName}
                                  placeholder="Nume ingredient..."
                                />
                                {isLoadingName ? (
                                  <div className="flex-shrink-0">
                                    <svg className="animate-spin h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleConfirmEditName(idx)}
                                      className="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 text-green-600 flex items-center justify-center hover:bg-green-200 transition-colors"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={handleCancelEditName}
                                      className="flex-shrink-0 w-7 h-7 rounded-full bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 transition-colors"
                                    >
                                      ✕
                                    </button>
                                  </>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => handleStartEditName(idx)}
                                className="flex items-center gap-1.5 group"
                                title="Apasă pentru a schimba ingredientul"
                              >
                                <p className="font-medium text-gray-800 text-sm truncate group-hover:text-blue-600 transition-colors">
                                  {ing.name}
                                </p>
                                <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            )}
                          </div>

                          {!isEditingName && (
                            editingGrams ? (
                              /* Mod editare grame: butoane +/- */
                              <div className="flex items-center gap-2 ml-3">
                                <button
                                  onClick={() => adjustGrams(idx, -10)}
                                  className="w-8 h-8 rounded-full bg-red-100 text-red-600 font-bold text-lg flex items-center justify-center hover:bg-red-200 transition-colors"
                                >
                                  −
                                </button>
                                <span className="w-16 text-center font-bold text-gray-800 text-sm">
                                  {grams}g
                                </span>
                                <button
                                  onClick={() => adjustGrams(idx, 10)}
                                  className="w-8 h-8 rounded-full bg-green-100 text-green-600 font-bold text-lg flex items-center justify-center hover:bg-green-200 transition-colors"
                                >
                                  +
                                </button>
                              </div>
                            ) : (
                              /* Mod vizualizare: afișează gramele */
                              <span className="ml-3 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">
                                {grams}g
                              </span>
                            )
                          )}
                        </div>

                        {/* Nutrition values - Editable */}
                        {isEditingNutrition ? (
                          <div className="bg-white rounded-lg p-3 border border-blue-200 space-y-2">
                            <p className="text-xs font-semibold text-blue-700 mb-2">✏️ Editează valorile per 100g:</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[10px] text-gray-500 font-medium">Calorii (kcal)</label>
                                <input
                                  type="number"
                                  value={editNutritionValues.calories}
                                  onChange={(e) => setEditNutritionValues({ ...editNutritionValues, calories: parseFloat(e.target.value) || 0 })}
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-bold text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] text-gray-500 font-medium">Proteine (g)</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={editNutritionValues.protein}
                                  onChange={(e) => setEditNutritionValues({ ...editNutritionValues, protein: parseFloat(e.target.value) || 0 })}
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-bold text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] text-gray-500 font-medium">Carbohidrați (g)</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={editNutritionValues.carbs}
                                  onChange={(e) => setEditNutritionValues({ ...editNutritionValues, carbs: parseFloat(e.target.value) || 0 })}
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-bold text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] text-gray-500 font-medium">Grăsimi (g)</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={editNutritionValues.fat}
                                  onChange={(e) => setEditNutritionValues({ ...editNutritionValues, fat: parseFloat(e.target.value) || 0 })}
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-bold text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 pt-1">
                              <button
                                onClick={handleCancelEditNutrition}
                                className="flex-1 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors font-medium"
                              >
                                Anulează
                              </button>
                              <button
                                onClick={() => handleConfirmEditNutrition(idx)}
                                className="flex-1 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-bold"
                              >
                                ✓ Salvează
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex gap-1.5 flex-wrap">
                              <span className="text-xs text-gray-500">{ingCal} kcal</span>
                              <span className="text-xs text-gray-400">·</span>
                              <span className="text-xs text-gray-500">{ingProt}g P</span>
                              <span className="text-xs text-gray-400">·</span>
                              <span className="text-xs text-gray-500">{ingCarbs}g C</span>
                              <span className="text-xs text-gray-400">·</span>
                              <span className="text-xs text-gray-500">{ingFat}g F</span>
                            </div>
                            <button
                              onClick={() => handleStartEditNutrition(idx)}
                              className="text-[10px] text-blue-500 hover:text-blue-700 font-medium flex items-center gap-0.5 transition-colors"
                              title="Editează valorile nutriționale"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              editează
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TOTAL NUTRIȚIONAL CALCULAT */}
            <div className="bg-white border-2 border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-600">
                  Total {hasIngredients ? `(${Object.values(ingredientGrams).reduce((a: number, b: number) => a + b, 0)}g)` : ''}
                </h4>
                {!hasIngredients && (
                  <button
                    onClick={handleStartEditTotalNutrition}
                    className="text-xs text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Editează
                  </button>
                )}
              </div>

              {editingTotalCalories ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Calorii (kcal)</label>
                      <input
                        type="number"
                        value={editTotalValues.calories}
                        onChange={(e) => setEditTotalValues({ ...editTotalValues, calories: parseFloat(e.target.value) || 0 })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-bold text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Proteine (g)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editTotalValues.protein}
                        onChange={(e) => setEditTotalValues({ ...editTotalValues, protein: parseFloat(e.target.value) || 0 })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-bold text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Carbohidrați (g)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editTotalValues.carbs}
                        onChange={(e) => setEditTotalValues({ ...editTotalValues, carbs: parseFloat(e.target.value) || 0 })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-bold text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Grăsimi (g)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editTotalValues.fat}
                        onChange={(e) => setEditTotalValues({ ...editTotalValues, fat: parseFloat(e.target.value) || 0 })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-bold text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingTotalCalories(false)}
                      className="flex-1 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors font-medium"
                    >
                      Anulează
                    </button>
                    <button
                      onClick={handleConfirmEditTotalNutrition}
                      className="flex-1 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-bold"
                    >
                      ✓ Salvează
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center bg-orange-50 rounded-lg p-2">
                    <div className="text-xl font-bold text-orange-600">{totals.calories}</div>
                    <div className="text-xs text-gray-500">kcal</div>
                  </div>
                  <div className="text-center bg-blue-50 rounded-lg p-2">
                    <div className="text-lg font-bold text-blue-600">{totals.protein}g</div>
                    <div className="text-xs text-gray-500">Prot</div>
                  </div>
                  <div className="text-center bg-yellow-50 rounded-lg p-2">
                    <div className="text-lg font-bold text-yellow-600">{totals.carbs}g</div>
                    <div className="text-xs text-gray-500">Carbo</div>
                  </div>
                  <div className="text-center bg-red-50 rounded-lg p-2">
                    <div className="text-lg font-bold text-red-600">{totals.fat}g</div>
                    <div className="text-xs text-gray-500">Grăsimi</div>
                  </div>
                </div>
              )}

              {/* Edit button for totals when has ingredients */}
              {hasIngredients && !editingTotalCalories && (
                <button
                  onClick={handleStartEditTotalNutrition}
                  className="w-full mt-2 text-xs text-blue-500 hover:text-blue-700 font-medium flex items-center justify-center gap-1 py-1 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Editează totalul manual
                </button>
              )}
            </div>

            {/* BUTOANE */}
            <div className="space-y-3 pt-2">
              <button
                onClick={handleDirectAdd}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all font-bold text-lg disabled:opacity-50"
              >
                {loading ? 'Adaug...' : '✓ Adaugă la Masă'}
              </button>

              <button
                onClick={handleEditBeforeAdd}
                className="w-full py-3 border-2 border-blue-300 text-blue-600 rounded-xl hover:bg-blue-50 transition-all font-medium"
              >
                ✏️ Editează în formular complet
              </button>

              <button
                onClick={onClose}
                className="w-full py-3 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 transition-all"
              >
                Anulează
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // MAIN SCANNER UI
  // ============================================================
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <span className="text-2xl">📸</span>
            Scanează Aliment cu AI
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" disabled={loading}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {!preview && !loading && (
            <>
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">🍽️</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Scanează alimentul/masa</h3>
                <p className="text-sm text-gray-600">
                  AI-ul va analiza imaginea, va identifica ingredientele și va estima gramajul fiecăruia
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleTakePhoto}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-3 font-medium"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Fă Poză Acum</span>
                </button>

                <button
                  onClick={handleUploadPhoto}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-3 font-medium"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Alege din Galerie</span>
                </button>

                <label className="block">
                  <input type="file" accept="image/*" onChange={handleFileInput} className="hidden" />
                  <div className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-500 transition-all flex items-center justify-center gap-3 cursor-pointer text-gray-600 hover:text-primary-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="font-medium">Încarcă Imagine</span>
                  </div>
                </label>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Sfaturi pentru poze mai bune:
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Fă poza de sus, perpendicular pe farfurie</li>
                  <li>• Asigură-te că există lumină bună</li>
                  <li>• Încadrează tot alimentul/masa în poză</li>
                  <li>• AI-ul va estima gramajul fiecărui ingredient</li>
                  <li>• <strong>Poți corecta</strong> orice ingredient detectat greșit</li>
                </ul>
              </div>
            </>
          )}

          {/* Loading */}
          {loading && (
            <div className="text-center py-12">
              {preview && <img src={preview} alt="Preview" className="w-full max-h-64 object-contain rounded-lg mb-6" />}
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-primary-50 rounded-full">
                <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="font-medium text-primary-900">AI analizează imaginea...</span>
              </div>
              <p className="text-sm text-gray-600 mt-3">Estimez ingredientele și gramajele...</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="text-center py-12">
              {preview && <img src={preview} alt="Preview" className="w-full max-h-64 object-contain rounded-lg mb-6 opacity-50" />}
              <div className="inline-flex p-4 rounded-full bg-red-100 mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Ups!</h3>
              <p className="text-gray-600 mb-6">
                {error.toLowerCase().includes('insufficient credits') || error.toLowerCase().includes('credite insuficiente')
                  ? 'Credite insuficiente pentru a analiza imaginea.'
                  : error}
              </p>
              {(error.toLowerCase().includes('insufficient credits') || error.toLowerCase().includes('credite insuficiente')) ? (
                <div className="flex flex-col gap-3 items-center">
                  <button
                    onClick={() => { onClose(); navigate('/extra-credits'); }}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
                  >
                    Cumpără credite
                  </button>
                  <button
                    onClick={() => { setError(null); setPreview(null); }}
                    className="px-6 py-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Încearcă din nou
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setError(null); setPreview(null); }}
                  className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Încearcă din nou
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
